import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogContent, DialogTitle, Grid, IconButton, makeStyles, Typography } from "@material-ui/core";
import productPageStyle from "features/products/styles";
import { useTranslation } from "react-i18next";
import SearchIcon from "@material-ui/icons/Search";
import { Autocomplete, CustomTextField } from "@totalsoft/rocket-webapp-components-deprecated";
import { QUOTE_WITH_PRODUCT_HISTORY_QUERY } from "features/quote/queries";
import { useQueryWithErrorHandling } from "hooks/errorHandling";
import { get, set, sequence, useChangeTrackingLens } from "@totalsoft/change-tracking-react";
import { emptyArray, emptyString } from "constants/defaultValues";
import ProductHistoryComponent from "./ProductHistoryComponent";
import { Close } from "@material-ui/icons";
import moment from "moment";

const useStyles = makeStyles(productPageStyle);

function ProductHistoriesContainer(props) {
  const { productHistoriesContainerProps, quoteId, showAmountsWithVAT, currencyCode } = props;
  const productTypes = props.productTypes.map((x) => ({ id: x.id, name: x.name }));
  const isOpen = productHistoriesContainerProps.open |> get;
  const { t } = useTranslation();
  const classes = useStyles();
  const [productHistoriesLens, , resetProductHistoriesLens] = useChangeTrackingLens(emptyArray);
  const [productType, setProductType] = useState(productHistoriesContainerProps.productTypeId |> get);

  const { data } = useQueryWithErrorHandling(QUOTE_WITH_PRODUCT_HISTORY_QUERY, {
    variables: { quoteId },
    skip: !isOpen || !quoteId,
    onCompleted: (data) => {
      if (!data) return;
      resetProductHistoriesLens(data?.quote.quoteProductHistories.filter((x) => x.productTypeId === productType));
    },
  });
  const initialProductHistories = data?.quote?.quoteProductHistories || emptyArray;

  const handleClose = useCallback(() => {
    (productHistoriesContainerProps.open |> set)(false);
  }, [productHistoriesContainerProps.open]);

  const handleSearchValuesChanged = useCallback(
    (event) => {
      const searchValue = event?.target?.value.toLowerCase() || emptyString;
      if (searchValue === emptyString) {
        resetProductHistoriesLens(initialProductHistories);
        return;
      }

      const filteredProducts = initialProductHistories.filter(
        (item) =>
          (!showAmountsWithVAT && item.downPaymentAmount?.toString().includes(searchValue)) ||
          (showAmountsWithVAT && item.downPaymentAmountWithVAT?.toString().includes(searchValue)) ||
          (item.residualValueVisible &&
            !showAmountsWithVAT &&
            item.residualValueAmount?.toString().includes(searchValue)) ||
          (item.residualValueVisible &&
            showAmountsWithVAT &&
            item.residualValueAmountWithVAT?.toString().includes(searchValue)) ||
          (item.firstName?.toString() + item.lastName?.toString())
            .toLowerCase()
            .includes(searchValue.replace(/\s+/g, "")) ||
          item.productName?.toString().toLowerCase().includes(searchValue) ||
          item.duration?.toString().includes(searchValue) ||
          moment(item.modifiedTimestamp)?.format("LLLL").toString().toLowerCase().includes(searchValue)
      );
      resetProductHistoriesLens(filteredProducts);
    },
    [initialProductHistories, resetProductHistoriesLens, showAmountsWithVAT]
  );

  const handleChangeProductType = useCallback(
    (event) => {
      if (!event || productType === event) return;
      setProductType(event);
      const filteredProducts = initialProductHistories.filter((item) => item.productTypeId === event);
      resetProductHistoriesLens(filteredProducts);
    },
    [initialProductHistories, resetProductHistoriesLens, productType]
  );

  return (
    <Dialog scroll="paper" maxWidth="md" fullWidth={true} open={isOpen} onClose={handleClose}>
      <DialogTitle>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <Typography variant="h4">{t("QuoteHistory.ModificationHistory")}</Typography>
          </Grid>
          <Grid item>
            <IconButton onClick={handleClose}>
              <Close />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <Grid container className={classes.searchBackground}>
        <Grid item xs={6}>
          <CustomTextField
            label={t("QuoteHistory.SearchFor")}
            fullWidth={true}
            onChange={handleSearchValuesChanged}
            endAdornment={<SearchIcon></SearchIcon>}
          ></CustomTextField>
        </Grid>
        <Grid item xs={1} />
        <Grid item xs={3}>
          <Autocomplete
            options={productTypes}
            valueKey="id"
            labelKey="name"
            value={productType}
            onChange={handleChangeProductType}
            label={t("QuoteHistory.ProductType")}
            simpleValue={true}
            noOptionsText={t("General.AutocompleteNoOptions")}
          ></Autocomplete>
        </Grid>
      </Grid>
      <DialogContent>
        {(productHistoriesLens |> sequence).map((quoteProductHistory) => (
          <ProductHistoryComponent
            key={quoteProductHistory.quoteProductId |> get}
            quoteProductHistory={quoteProductHistory}
            currencyCode={currencyCode}
            showAmountsWithVAT={showAmountsWithVAT}
            onClose={handleClose}
          />
        ))}
      </DialogContent>
    </Dialog>
  );
}

ProductHistoriesContainer.propTypes = {
  productTypes: PropTypes.array.isRequired,
  productHistoriesContainerProps: PropTypes.object.isRequired,
  quoteId: PropTypes.number,
  showAmountsWithVAT: PropTypes.bool.isRequired,
  currencyCode: PropTypes.string.isRequired,
};

export default ProductHistoriesContainer;
